// src/components/flight-data/BrockTab.js
import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { 
  isOvernightFlight, 
  calculateConnectionTime, 
  getConnectionCategory
} from '../../utils/connectionUtils';
import ConnectionParametersInfo from './ConnectionParametersInfo';
import ConnectionLegend from './ConnectionLegend';
import BinAccordion from './BinAccordion';

/**
 * Component for the Brock/SmartSuite tab
 */
function BrockTab({ brockData, params, inboundETA }) {
  const bins = brockData?.bins || {};
  const loadSummary = brockData.loadSummary || {};
  const brockInboundFlightNumber = brockData.inboundFlightNumber || '';
  const brockInboundCity = brockData.inboundCity || '';

  // A helper to see if a bin is non-empty
  const isBinNonEmpty = (binObj) => {
    if (!binObj) return false;
    if (binObj.flights && binObj.flights.length > 0) return true;
    if (binObj.total && (binObj.total.totalBags || 0) > 0) return true;
    if (binObj.terminating && (binObj.terminating.totalBags || 0) > 0) return true;
    return false;
  };

  // Gather all bin names
  const binNames = Object.keys(bins);
  
  // Define default expanded bins based on whether they're non-empty
  const defaultExpanded = binNames.filter((binName) => isBinNonEmpty(bins[binName]));

  // State for tracking expanded bins
  const [expandedBins, setExpandedBins] = useState(defaultExpanded);

  // Toggles a given bin in or out of expandedBins
  const handleAccordionChange = (binName) => (event, isExpanding) => {
    setExpandedBins((prev) => {
      if (isExpanding) {
        // expand
        if (!prev.includes(binName)) {
          return [...prev, binName];
        }
        return prev;
      } else {
        // collapse
        return prev.filter((b) => b !== binName);
      }
    });
  };

  // Process bins for sorting flights within each bin
  const processedBins = {};
    
  Object.entries(bins).forEach(([binName, binObj]) => {
    // Deep clone the bin object to avoid modifying the original
    const processedBin = JSON.parse(JSON.stringify(binObj));
    
    // Sort flights by connection time
    if (processedBin.flights && processedBin.flights.length > 0) {
      // Check for overnight flights in each bin's flights
      processedBin.flights.forEach(flight => {
        // Use the specific overnight detection function
        flight.isOvernight = isOvernightFlight(flight);
        
        // Calculate connection time with overnight flag
        flight.connectionTime = calculateConnectionTime(flight.est, inboundETA, flight.isOvernight);
        flight.connectionCategory = getConnectionCategory(
          flight.connectionTime, 
          params, 
          flight.isOvernight,
          { scheduledTime: flight.sch, inboundETA: inboundETA }
        );
        
        // Make sure freightItems is available
        if (!flight.freightItems) {
          flight.freightItems = [];
        }
      });
      
      // Sort flights into category groups
      const hotAndPriorityFlights = processedBin.flights.filter(f => 
        f.connectionCategory === 'hot' || f.connectionCategory === 'priority'
      );
      const normalFlights = processedBin.flights.filter(f => 
        f.connectionCategory === 'normal'
      );
      const coldFlights = processedBin.flights.filter(f => 
        f.connectionCategory === 'cold'
      );
      
      // Sort each group by ETD
      hotAndPriorityFlights.sort((a, b) => (a.est || '').localeCompare(b.est || ''));
      normalFlights.sort((a, b) => (a.est || '').localeCompare(b.est || ''));
      coldFlights.sort((a, b) => (a.est || '').localeCompare(b.est || ''));
      
      // Combine the groups with hot/priority first, normal, then cold
      processedBin.flights = [...hotAndPriorityFlights, ...normalFlights, ...coldFlights];
    }
    
    processedBins[binName] = processedBin;
  });

  return (
    <div style={{ marginTop: '1rem' }}>
      <h3 style={{ color: '#FF0000' }}>Brock / SmartSuite</h3>
      
      {/* Connection Parameters Info */}
      <ConnectionParametersInfo params={params} color="#FF0000" />

      <h4 style={{ color: '#FF0000' }}>Inbound Flight Info</h4>
      <p>Flight: {brockInboundFlightNumber} {brockInboundCity}</p>

      {/* Load Summary */}
      <TableContainer component={Paper} sx={{ marginBottom: '2rem', backgroundColor: 'transparent' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>Total Cargo</TableCell>
              <TableCell sx={{ color: '#fff' }}>Total Bags</TableCell>
              <TableCell sx={{ color: '#fff' }}>Terminating</TableCell>
              <TableCell sx={{ color: '#fff' }}>Thru</TableCell>
              <TableCell sx={{ color: '#fff' }}>Transfer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>{loadSummary.totalCargo}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{loadSummary.totalBags}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{loadSummary.terminating}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{loadSummary.thru}</TableCell>
              <TableCell sx={{ color: '#fff' }}>{loadSummary.transfer}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Legend Box */}
      <ConnectionLegend params={params} />

      {/* Each bin displayed as an Accordion */}
      {Object.entries(processedBins).map(([binName, binObj]) => (
        <BinAccordion
          key={binName}
          binName={binName}
          binObj={binObj}
          isExpanded={expandedBins.includes(binName)}
          onToggle={handleAccordionChange(binName)}
          params={params}
          inboundETA={inboundETA}
        />
      ))}

    </div>
  );
}

export default BrockTab;